import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getParticipantsByGender } from '../../services/participantService';
import { getCompetitionById } from '../../services/competitionService';
import { createRegistration } from '../../services/registrationService';
import { getActiveSportsYear } from '../../services/sportsYearService';
import { Participant, Competition } from '../../types/models';
import { ArrowLeft, Save, Search } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { format } from 'date-fns';

const CompetitionRegistration: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  
  const [competition, setCompetition] = useState<Competition | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [selectedParticipants, setSelectedParticipants] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        
        // First fetch the competition details
        const competitionData = await getCompetitionById(id);
        if (!competitionData) throw new Error('Competition not found');
        
        setCompetition(competitionData);
        
        // Get all participants matching the gender category
        const participantsData = await getParticipantsByGender(
          competitionData.gender_category === 'M' ? 'Male' : 
          competitionData.gender_category === 'F' ? 'Female' : 'All'
        );

        // For team events with all age groups (age_group_id is null)
        if (competitionData.is_team_event && !competitionData.age_group_id) {
          // Include all participants matching the gender
          setParticipants(participantsData);
        } else {
          // Filter participants by birth year based on age group birth years
          const filteredParticipants = participantsData.filter(participant => {
            const birthYear = new Date(participant.date_of_birth).getFullYear();
            return competitionData.age_group?.birth_years.includes(birthYear);
          });

          setParticipants(filteredParticipants);
        }
      } catch (error: any) {
        console.error('Error fetching data:', error);
        setError(error.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const filteredParticipants = participants.filter(participant => {
    const fullName = `${participant.first_name} ${participant.last_name}`.toLowerCase();
    const searchLower = searchTerm.toLowerCase();
    
    return fullName.includes(searchLower) ||
           participant.house?.house_name.toLowerCase().includes(searchLower);
  });

  const handleParticipantToggle = (participantId: string) => {
    setSelectedParticipants(prev => {
      if (prev.includes(participantId)) {
        return prev.filter(id => id !== participantId);
      } else {
        return [...prev, participantId];
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !competition) return;

    try {
      setSaving(true);
      setError(null);

      const currentYear = await getActiveSportsYear();

      // Check for existing registrations
      const { data: existingRegistrations, error: registrationError } = await supabase
        .from('student_event_registrations')
        .select('student_id')
        .eq('event_id', id)
        .eq('competition_year', currentYear)
        .in('student_id', selectedParticipants);

      if (registrationError) throw registrationError;

      // Filter out participants who are already registered
      const existingParticipantIds = existingRegistrations?.map(reg => reg.student_id) || [];
      const newParticipants = selectedParticipants.filter(
        participantId => !existingParticipantIds.includes(participantId)
      );

      if (newParticipants.length === 0) {
        const existingNames = participants
          .filter(p => existingParticipantIds.includes(p.student_id))
          .map(p => `${p.first_name} ${p.last_name}`)
          .join(', ');
        setError(t('competitions.registration.errors.alreadyRegistered', { names: existingNames }));
        return;
      }

      // Register all selected participants
      await Promise.all(
        newParticipants.map(participantId =>
          createRegistration({
            student_id: participantId,
            event_id: id,
            competition_year: currentYear,
            status: 'registered'
          }).catch(error => {
            // If this is a competition limit error, throw it to be handled
            if (error.message.includes('has already registered for the maximum allowed number of individual competitions')) {
              throw error;
            }
            throw error;
          })
        )
      );

      navigate(`/competitions/${id}`);
    } catch (error: any) {
      console.error('Error registering participants:', error);
      setError(error.message || 'Failed to register participants');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!competition) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">Competition not found</div>
        <button
          onClick={() => navigate('/competitions')}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
        >
          {t('competitions.registration.actions.cancel')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <button
            onClick={() => navigate(-1)}
            className="mr-4 inline-flex items-center p-2 border border-transparent rounded-full shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <ArrowLeft className="h-5 w-5" aria-hidden="true" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('competitions.registration.title')}</h1>
            <p className="mt-1 text-sm text-gray-500">{competition.event_name}</p>
          </div>
        </div>
      </div>

      {error && (
        <div className={`border-l-4 p-4 ${
          error.startsWith('Note:') ? 'bg-yellow-50 border-yellow-500' : 'bg-red-50 border-red-500'
        }`}>
          <div className="flex">
            <div className="ml-3">
              <p className={`text-sm ${
                error.startsWith('Note:') ? 'text-yellow-700' : 'text-red-700'
              }`}>{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
          <div className="flex items-center">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('competitions.registration.search.placeholder')}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="overflow-y-auto max-h-96">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      checked={selectedParticipants.length === filteredParticipants.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedParticipants(filteredParticipants.map(p => p.student_id));
                        } else {
                          setSelectedParticipants([]);
                        }
                      }}
                    />
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('competitions.registration.table.name')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('competitions.registration.table.birthYear')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('competitions.registration.table.house')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('competitions.registration.table.gender')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredParticipants.map((participant) => (
                  <tr 
                    key={participant.student_id}
                    className={`hover:bg-gray-50 ${
                      selectedParticipants.includes(participant.student_id) ? 'bg-blue-50' : ''
                    }`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        checked={selectedParticipants.includes(participant.student_id)}
                        onChange={() => handleParticipantToggle(participant.student_id)}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {participant.first_name} {participant.last_name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {format(new Date(participant.date_of_birth), 'yyyy')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div 
                          className="h-3 w-3 rounded-full mr-2" 
                          style={{ backgroundColor: participant.house?.house_color }}
                        />
                        <div className="text-sm text-gray-900">{participant.house?.house_name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        participant.gender === 'M' ? 'bg-blue-100 text-blue-800' : 'bg-pink-100 text-pink-800'
                      }`}>
                        {participant.gender === 'M' ? t('participants.filters.male') : t('participants.filters.female')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
            <button
              type="button"
              onClick={() => navigate(`/competitions/${id}`)}
              className="mr-3 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {t('competitions.registration.actions.cancel')}
            </button>
            <button
              type="submit"
              disabled={saving || selectedParticipants.length === 0}
              className="inline-flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? t('competitions.registration.actions.registering') : t('competitions.registration.actions.register')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompetitionRegistration;